import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { FormControlsOf } from '@examdojo/angular/forms';
import { ToastService } from '@examdojo/core/toast';
import { catchError, distinctUntilChanged, EMPTY, filter, map, switchMap, tap } from 'rxjs';
import { SourceStoreModel, SourceUpdateModel } from '../source.model';
import { SourceQuery } from '../source.query';
import { SourceService } from '../source.service';

export type SourceEditorForm = FormControlsOf<{
  name: SourceStoreModel['name'];
  source_type: SourceStoreModel['source_type'];
  document_type: SourceStoreModel['document_type'];
  source_ref: string;
  raw_source: string;
  extracted_content: string;
  processed_content: string;
  parsing_instructions: string;
  tier: SourceStoreModel['tier'];
  metadata: string;
  id: SourceStoreModel['id'];
}>;

const notRequiredJsonValidator = (control: AbstractControl): ValidationErrors | null => {
  try {
    if (control.value) {
      JSON.parse(control.value);
    }
    return null;
  } catch (e) {
    return { invalidJson: true };
  }
};

@Injectable()
export class SourceEditorFormService {
  constructor(
    private readonly sourceQuery: SourceQuery,
    private readonly sourceService: SourceService,
    private readonly toastService: ToastService,
  ) {
    this.fetchSourceOnIdChange().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly activeSourceFetched$ = this.sourceQuery.selectActive().pipe(map(Boolean), distinctUntilChanged());

  readonly sourceForm = new FormGroup<SourceEditorForm>({
    source_type: new FormControl('OFFICIAL_PAST_EXAM', {
      nonNullable: true,
    }),
    document_type: new FormControl('PDF', {
      nonNullable: true,
    }),
    raw_source: new FormControl<string>('', {
      nonNullable: true,
      validators: [notRequiredJsonValidator, Validators.required],
    }),
    source_ref: new FormControl<string>('', {
      nonNullable: true,
      validators: [notRequiredJsonValidator, Validators.required],
    }),
    extracted_content: new FormControl<string>('', {
      nonNullable: true,
      validators: [notRequiredJsonValidator],
    }),
    processed_content: new FormControl<string>('', {
      nonNullable: true,
      validators: [notRequiredJsonValidator],
    }),
    parsing_instructions: new FormControl<string>('', {
      nonNullable: true,
      validators: [notRequiredJsonValidator],
    }),
    tier: new FormControl<number>(0, {
      nonNullable: true,
    }),
    metadata: new FormControl<string>('', {
      nonNullable: true,
      validators: [notRequiredJsonValidator],
    }),
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    id: new FormControl<SourceStoreModel['id']>(
      { value: '', disabled: true },
      {
        nonNullable: true,
      },
    ),
  });

  readonly statusFormCtrl = new FormControl<SourceStoreModel['status']>('DRAFT', {
    nonNullable: true,
    validators: [Validators.required],
  });

  getMainFormValue(): SourceUpdateModel | null {
    const formValue = this.sourceForm.getRawValue();
    let sourceModel: SourceUpdateModel;
    try {
      sourceModel = {
        ...formValue,
        raw_source: formValue.raw_source ? JSON.parse(formValue.raw_source) : null,
        source_ref: formValue.source_ref ? JSON.parse(formValue.source_ref) : null,
        extracted_content: formValue.extracted_content ? JSON.parse(formValue.extracted_content) : null,
        processed_content: formValue.processed_content ? JSON.parse(formValue.processed_content) : null,
        parsing_instructions: formValue.parsing_instructions ? JSON.parse(formValue.parsing_instructions) : null,
        metadata: formValue.metadata ? JSON.parse(formValue.metadata) : null,
      };

      return sourceModel;
    } catch (error) {
      this.toastService.error('Failed to parse JSON when saving source');
      return null;
    }
  }

  private fetchSourceOnIdChange() {
    return this.sourceQuery.selectActiveId().pipe(
      filter(Boolean),
      distinctUntilChanged(),
      switchMap((id) => this.sourceService.fetch(id).pipe(catchError(() => EMPTY))),
      tap((source) => {
        this.statusFormCtrl.setValue(source.status, {
          emitEvent: false,
        });

        this.sourceForm.setValue({
          source_type: source.source_type,
          document_type: source.document_type,
          source_ref: source.source_ref ? JSON.stringify(source.source_ref) : '',
          raw_source: source.raw_source ? JSON.stringify(source.raw_source) : '',
          id: source.id,
          extracted_content: source.extracted_content ? JSON.stringify(source.extracted_content) : '',
          processed_content: source.processed_content ? JSON.stringify(source.processed_content) : '',
          parsing_instructions: source.parsing_instructions ? JSON.stringify(source.parsing_instructions) : '',
          tier: source.tier,
          metadata: source.metadata ? JSON.stringify(source.metadata) : '',
          name: source.name,
        });
      }),
    );
  }
}
